import { Clock, ExternalLink, MapPin, Shirt, Sparkles, CheckCircle2 } from "lucide-react";
import { Reveal, Floral, GoldDivider } from "../components/Decor";
import { ASSESSORIA, DICAS, EVENT } from "../constants/site";

const TIPS = [
  {
    nome: "Mari Peron",
    texto:
      "Recomendamos a Mari Peron para quem busca elegância, qualidade e atendimento personalizado na escolha do traje feminino. Como um carinho especial aos nossos convidados, a loja oferece 10% de desconto na locação. Basta informar que você é convidado do casamento de Talita Henriques para aproveitar o benefício.",
    endereco: "Rua Ismenia Vieira de Alencar, 59 - Capão Raso, Curitiba - PR",
    site: "https://www.mariperon.com.br/",
    maps: "https://www.google.com/maps/search/?api=1&query=-25.5011549%2C-49.288570899999996",
  },
  {
    nome: "Nilma Noivas",
    texto:
      "Recomendamos a Nilma Noivas para quem procura praticidade, elegância e uma grande variedade de opções para toda a família. Como um carinho especial aos nossos convidados, a loja oferece 10% de desconto na locação de trajes femininos, masculinos e infantis. Basta informar que você é convidado do casamento de Talita Henriques para aproveitar o benefício.",
    endereco: "Tv. José Ziliotto, 76 - Centro, São José dos Pinhais - PR",
    site: "https://nilmanoivas.com.br/",
    maps: "https://www.google.com/maps/search/?api=1&query=-25.537121499999998%2C-49.2082851",
  },
];

export default function CerimoniaRecepcao() {
  return (
    <div className="relative overflow-hidden pt-28" data-testid="page-cerimonia">
      <Floral variant="top" mirrored className="absolute -right-20 -top-4 w-72 opacity-80" />
      <header className="relative mx-auto max-w-3xl px-5 text-center">
        <p className="eyebrow">O grande dia</p>
        <h1 className="mt-4 font-script text-6xl text-ink sm:text-7xl">Cerimônia e Recepção</h1>
        <GoldDivider className="mt-8" />
      </header>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <Reveal>
          <div className="paper-card gold-frame grid items-stretch gap-0 overflow-hidden md:grid-cols-2" data-testid="event-card">
            <div className="flex flex-col justify-center p-8 text-left sm:p-12">
              <p className="eyebrow">Sábado, 09 de Janeiro de 2027</p>
              <h2 className="mt-4 font-script text-5xl text-ink">{EVENT.local}</h2>
              <div className="mt-6 space-y-1 font-sans text-sm font-light text-ink/80">
                <p>{EVENT.endereco1}</p>
                <p>{EVENT.endereco2}</p>
                <p className="flex items-center gap-2 pt-3 text-sage-dark">
                  <Clock size={14} /> A cerimônia terá início às 16h
                </p>
              </div>
              <a
                href={EVENT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-maps-btn"
                className="btn-gold mt-8 w-fit"
              >
                <MapPin size={14} /> Abrir no Google Maps
              </a>
            </div>
            <div className="relative min-h-[280px]">
              <img
                src="/images/local-evento.png"
                alt="Dom Guilherme Eventos"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="event-photo"
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="paper-card gold-frame mt-10 overflow-hidden" data-testid="event-map">
            <iframe
              title="Mapa — Dom Guilherme Eventos"
              data-testid="event-map-embed"
              src="https://maps.google.com/maps?q=-25.3305005,-49.3100708&z=15&output=embed"
              width="100%"
              height="400"
              style={{ border: 0, filter: "sepia(0.18) saturate(0.85)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
            <p className="px-5 py-4 text-center font-sans text-xs font-light text-muted">
              Dom Guilherme Eventos — R. Ademar Castilho - S/Nº (final da rua), Almirante Tamandaré - PR
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-gold-border bg-paperdeep/60 py-16" data-testid="dicas">
        <header className="mx-auto max-w-3xl px-5 text-center">
          <h2 className="font-serif text-4xl uppercase tracking-[0.14em] text-ink">{DICAS.title}</h2>
          <p className="mx-auto mt-4 max-w-xl font-sans text-sm font-light text-muted">{DICAS.subtitle}</p>
        </header>

        <div className="mx-auto mt-12 grid max-w-4xl gap-6 px-5 md:grid-cols-2">
          <Reveal>
            <div className="paper-card h-full p-8 text-left" data-testid="dica-local">
              <p className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-[0.26em] text-sage-dark">
                <MapPin size={15} /> Local
              </p>
              <p className="mt-4 font-sans text-sm font-light leading-loose text-ink/80">
                A celebração acontecerá no <strong>Dom Guilherme Eventos</strong>, em <strong>Almirante Tamandaré/PR.</strong>
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                Escolhemos esse espaço com muito carinho para celebrar um dos momentos mais importantes das nossas vidas e estamos ansiosos para recebê-los.
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                O local conta com <strong>amplo estacionamento gratuito</strong> e <strong>fácil acesso pelas principais avenidas,</strong> proporcionando conforto e comodidade a todos os convidados.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.06}>
            <div className="paper-card h-full p-8 text-left" data-testid="dica-horario">
              <p className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-[0.26em] text-sage-dark">
                <Clock size={15} /> Horário
              </p>
              <p className="mt-4 font-sans text-sm font-light leading-loose text-ink/80">
                A cerimônia terá início <strong>às 16h.</strong>
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                Pedimos a gentileza de chegarem em tempo hábil para que possam acompanhar cada momento da cerimônia e viver esse instante conosco desde o início.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="paper-card h-full p-8 text-left" data-testid="dica-traje">
              <p className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-[0.26em] text-sage-dark">
                <Shirt size={15} /> Traje
              </p>
              <p className="mt-4 font-sans text-sm font-light leading-loose text-ink/80">
                Para tornar essa ocasião ainda mais especial, o traje sugerido é <strong>social completo.</strong>
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                • <strong>Damas:</strong> vestidos longos ou midi, com cores, brilho e muita elegância, serão muito bem-vindos. Pedimos apenas, gentilmente, que evitem a cor <strong>verde-esmeralda</strong>, reservada exclusivamente às nossas madrinhas e também <strong>branco e tons próximos</strong>, reservados à noiva.
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                • <strong>Cavalheiros:</strong> recomendamos o uso de <strong>terno e gravata</strong>, em harmonia com a proposta da celebração.
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                Agradecemos pela colaboração e por fazerem parte deste momento tão significativo para nós.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="paper-card h-full p-8 text-left" data-testid="dica-confirmação">
              <p className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-[0.26em] text-sage-dark">
                <CheckCircle2 size={15} /> Confirmação de Presença
              </p>
              <p className="mt-4 font-sans text-sm font-light leading-loose text-ink/80">Sua presença será muito especial para nós.</p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                A confirmação poderá ser realizada <strong>aqui pelo site</strong> ou <strong>via WhatsApp da nossa assessoria</strong>, pelo número{" "}
                <a href={ASSESSORIA.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="dicas-whatsapp-link" className="font-medium text-sage-dark underline underline-offset-2">
                  {ASSESSORIA.numeroLabel}
                </a>
                , <strong>até o dia 03/12/2026</strong>.
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                Pedimos a gentileza de confirmar sua presença dentro desse prazo para que possamos organizar cada detalhe com todo o carinho.
              </p>
              <p className="mt-3 font-sans text-sm font-light leading-loose text-ink/80">
                Desde já, agradecemos por fazer parte deste momento tão importante em nossas vidas. Será uma alegria celebrar com você.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-14 max-w-4xl px-5" data-testid="dica-traje-desconto">
          <GoldDivider className="mb-10" />
          <Reveal>
            <p className="flex items-center justify-center gap-2 text-center font-sans text-xs font-semibold uppercase tracking-[0.26em] text-sage-dark">
              <Sparkles size={15} /> Dica para o traje
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-center font-sans text-sm font-light leading-loose text-ink/80">
              Pensando na comodidade dos nossos convidados, conseguimos um <strong>desconto exclusivo de 10% na locação de trajes</strong> em lojas parceiras. Para utilizar o benefício, basta informar que você é <strong>convidado do casamento de Talita Henriques.</strong>
            </p>
          </Reveal>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {TIPS.map((t, i) => (
              <Reveal key={t.nome} delay={0.06 * i}>
                <div className="paper-card h-full p-7 text-left" data-testid={`parceira-${i}`}>
                  <h3 className="font-serif text-2xl text-ink">{t.nome}</h3>
                  <p className="mt-3 flex-1 font-sans text-sm font-light leading-loose text-ink/80">{t.texto}</p>
                  <p className="mt-4 font-sans text-xs font-light text-muted">{t.endereco}</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a href={t.site} target="_blank" rel="noopener noreferrer" data-testid={`parceira-site-${i}`} className="btn-outline px-5 py-2.5 text-[10px]">
                      <ExternalLink size={12} /> Site da loja
                    </a>
                    <a href={t.maps} target="_blank" rel="noopener noreferrer" data-testid={`parceira-maps-${i}`} className="btn-outline px-5 py-2.5 text-[10px]">
                      <MapPin size={12} /> Como chegar
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
